import { ref, computed, watch, type WritableComputedRef } from 'vue';
import { app } from '@ibgroup-tws-webapps/ratio.ui';

export type PriceSide = 'bid' | 'ask';

const STORAGE_KEY = 'forecast_priceSide';
const SETTINGS_LOAD_TIMEOUT = 5000;
const SETTINGS_FIELDS = ['priceSide'];

function isValidPriceSide(value: unknown): value is PriceSide {
  return value === 'bid' || value === 'ask';
}

function readLocalStorage(): PriceSide | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isValidPriceSide(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeLocalStorage(value: PriceSide) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // storage full or blocked
  }
}

const BROADCAST_CHANNEL = 'forecast_priceSide';

const priceSide = ref<PriceSide>(readLocalStorage() ?? 'ask');
const settingsCache = ref<Record<string, unknown>>({});
const isLoadingSettings = ref(true);
let initialized = false;

const channel = new BroadcastChannel(BROADCAST_CHANNEL);
channel.onmessage = (event: MessageEvent) => {
  if (isValidPriceSide(event.data)) {
    priceSide.value = event.data;
    writeLocalStorage(event.data);
  }
};

function waitForSettingsLoad(): Promise<boolean> {
  if (!isLoadingSettings.value) return Promise.resolve(true);
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      isLoadingSettings.value = false;
      resolve(false);
    }, SETTINGS_LOAD_TIMEOUT);
    const unwatch = watch(
      () => isLoadingSettings.value,
      (loading) => {
        if (!loading) {
          clearTimeout(timeout);
          unwatch();
          resolve(true);
        }
      },
    );
  });
}

async function saveSettings(newSettings: Record<string, unknown>) {
  const loadSuccessful = await waitForSettingsLoad();
  if (!loadSuccessful) return;
  const mergedSettings = { ...settingsCache.value, ...newSettings };
  await app.send({
    action: 'save_settings_local',
    data: mergedSettings,
  });
  settingsCache.value = mergedSettings;
}

function initSettings() {
  if (initialized) return;
  initialized = true;

  app.send?.({
    action: 'load_settings_local',
    fields: SETTINGS_FIELDS,
  });

  const loadTimeout = window.setTimeout(() => {
    unsubscribeBridge?.();
    isLoadingSettings.value = false;
  }, SETTINGS_LOAD_TIMEOUT);

  const bridge = app.getBridge?.();
  const unsubscribeBridge = bridge?.onMessage((msg) => {
    if (msg.action === 'load_settings_local') {
      clearTimeout(loadTimeout);
      unsubscribeBridge?.();
      const data = (msg.data || {}) as Record<string, unknown>;
      settingsCache.value = data;
      if (isValidPriceSide(data.priceSide)) {
        priceSide.value = data.priceSide;
        writeLocalStorage(data.priceSide);
      }
      isLoadingSettings.value = false;
    }
  });
}

export function usePriceSideSetting(): { priceSide: WritableComputedRef<PriceSide> } {
  initSettings();

  const priceSideModel = computed({
    get: () => priceSide.value,
    set: (value: PriceSide) => {
      priceSide.value = value;
      writeLocalStorage(value);
      channel.postMessage(value);
      saveSettings({ priceSide: value });
    },
  });

  return {
    priceSide: priceSideModel,
  };
}
